// PriceListCatalogPage.jsx
import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from './config.js';
import { useParams, useNavigate } from 'react-router-dom';
import axios from "axios";
import {
  Container,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
  useMediaQuery,
  useTheme,
  Stack,
  Paper,
} from '@mui/material';
import { 
  ArrowBack, 
  Download, 
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  DownloadForOffline,
  Visibility,
  Description,
  AttachMoney,
  Inventory,
  LocalOffer,
  Store,
  FolderOff,
} from '@mui/icons-material';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import useAuditPageView from '../hooks/useAuditPageView';

const PriceListCatalogPage = () => {
  const { id, category } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState({});
  const [selectedFiles, setSelectedFiles] = useState({});

  useEffect(() => {
    fetchSupplierData();
  }, [id]);

  useAuditPageView(`Viewed catalog page (category=${category})`);

  // Initialize selected files when supplier data is loaded
  useEffect(() => {
    if (supplier?.documents) {
      initializeSelectedFiles(supplier.documents);
    }
  }, [supplier]);

  const initializeSelectedFiles = (documents) => {
    const grouped = groupFilesByCategory(documents);
    const initialSelectedFiles = {};
    
    Object.keys(grouped).forEach(category => {
      initialSelectedFiles[category] = grouped[category].map(() => true); // All files selected by default
    });
    
    setSelectedFiles(initialSelectedFiles);
  };

  const fetchSupplierData = async () => {
    console.log(`Fetching ID: ${category}`);
    try {
      setLoading(true);
      setError("");

      const token = localStorage.getItem("access"); // use access token
      if (!token) throw new Error("No access token found. Please login.");

      // Make the request to Django
      const response = await axios.get(`${API_BASE_URL}/api/catalogs/`, {
        params: { type: category }, // query parameter
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Transform backend response to match your mock structure
      const data = response.data;

      const formattedData = {
        category: data.category,
        documents: data.documents.map((doc) => ({
          id: doc.id,
          name: doc.name,
          category: doc.category || "Price Lists",
          fileType: doc.file_type || doc.fileType,
          size: doc.file_size || doc.size,
          uploadedAt: doc.uploaded_at || doc.uploadedAt,
          version: doc.version || "",
          url: doc.url || (doc.file ? doc.file : ""), // use file URL if available
        })),
      };

      setSupplier(formattedData);
    } catch (error) {
      setError(error.response?.data?.detail || error.message);
      navigate("/");
      console.error("Error fetching supplier data:", error.response?.data || error);
    } finally {
      setLoading(false);
    }
  };

  const groupFilesByCategory = (files) => {
    const grouped = {};
    
    files?.forEach(file => {
      if (!grouped[file.category]) {
        grouped[file.category] = [];
      }
      grouped[file.category].push(file);
    });
    
    return grouped;
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleDownloadSingle = async (fileUrl, fileName, category, fileIndex) => {
    try {
      setDownloading(prev => ({ ...prev, [`${category}_${fileIndex}`]: true }));
      window.open(fileUrl, '_blank');
    } finally {
      setTimeout(() => {
        setDownloading(prev => ({ ...prev, [`${category}_${fileIndex}`]: false }));
      }, 1000);
    }
  };

  const handleDownloadCategoryAsZip = async (category, files) => {
    try {
      const downloadKey = `${category}_zip`;
      setDownloading(prev => ({ ...prev, [downloadKey]: true }));
      
      const zip = new JSZip();
      const categoryFolder = zip.folder(category.replace(/\s+/g, '_'));
      
      // Download each selected file and add to zip
      const downloadPromises = files.map(async (file, index) => {
        if (!selectedFiles[category]?.[index]) return;
        
        try {
          // In a real app, fetch from actual URL
          // const response = await fetch(file.url);
          // const blob = await response.blob();
          
          // Mock blob for demonstration
          const mockBlob = new Blob([`Mock content for ${file.name}`], { 
            type: file.fileType === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
          });
          categoryFolder.file(`${file.name}.${file.fileType}`, mockBlob);
        } catch (error) {
          console.error(`Error downloading ${file.url}:`, error);
        }
      });
      
      await Promise.all(downloadPromises);
      
      // Generate and download zip
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `${supplier.category}_${category.replace(/\s+/g, '_')}.zip`);
      
    } catch (error) {
      console.error('Error creating zip:', error);
      alert('Failed to create zip file. Please try again.');
    } finally {
      setDownloading(prev => ({ ...prev, [`${category}_zip`]: false }));
    }
  };

  const handleDownloadAllAsZip = async () => {
    try {
      setDownloading(prev => ({ ...prev, all: true }));
      
      const zip = new JSZip();
      const supplierFolder = zip.folder(supplier.category.replace(/\s+/g, '_'));
      
      const groupedFiles = groupFilesByCategory(supplier.documents);
      const downloadPromises = [];
      
      Object.entries(groupedFiles).forEach(([category, files]) => {
        const selectedFilesInCategory = files.filter((file, index) => selectedFiles[category]?.[index]);
        
        if (selectedFilesInCategory.length > 0) {
          const categoryFolder = supplierFolder.folder(category.replace(/\s+/g, '_'));
          
          selectedFilesInCategory.forEach((file) => {
            downloadPromises.push(
              (async () => {
                try {
                  // Mock blob for demonstration
                  const mockBlob = new Blob([`Mock content for ${file.name}`], { 
                    type: file.fileType === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
                  });
                  categoryFolder.file(`${file.name}.${file.fileType}`, mockBlob);
                } catch (error) {
                  console.error(`Error downloading ${file.url}:`, error);
                }
              })()
            );
          });
        }
      });
      
      await Promise.all(downloadPromises);
      
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `${supplier.category}_PriceLists_Catalogs.zip`);
      
    } catch (error) {
      console.error('Error creating zip:', error);
      alert('Failed to create zip file. Please try again.');
    } finally {
      setDownloading(prev => ({ ...prev, all: false }));
    }
  };

  const toggleFileSelection = (category, index) => {
    setSelectedFiles(prev => ({
      ...prev,
      [category]: prev[category].map((selected, i) => 
        i === index ? !selected : selected
      )
    }));
  };

  const toggleAllFilesInCategory = (category, selectAll) => {
    setSelectedFiles(prev => ({
      ...prev,
      [category]: prev[category].map(() => selectAll)
    }));
  };

  const getFileIcon = (file) => {
    if (file.fileType === 'pdf') {
      return <PdfIcon color="error" fontSize={isMobile ? "medium" : "large"} />;
    } else if (file.fileType === 'excel') {
      return <ExcelIcon color="success" fontSize={isMobile ? "medium" : "large"} />;
    }
    return <Description />;
  };

  const getSelectedFileCount = () => {
    if (!selectedFiles || Object.keys(selectedFiles).length === 0) return 0;
    
    let count = 0;
    Object.entries(selectedFiles).forEach(([category, selections]) => {
      count += selections.filter(selected => selected).length;
    });
    return count;
  };

  const getTotalFileCount = () => {
    return supplier?.documents?.length || 0;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month}. ${day}, ${year}`;
  };

  const renderNoCatalogsMessage = () => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        p: isMobile ? 2 : 3,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: isMobile ? 4 : 6,
          textAlign: 'center',
          backgroundColor: 'grey.50',
          borderRadius: 2,
          border: '1px dashed',
          borderColor: 'grey.300',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          maxWidth: 600,
          width: '100%',
          mx: 'auto',
        }}
      >
        <FolderOff
          sx={{
            fontSize: isMobile ? 48 : 64,
            color: 'grey.400',
            mb: 2,
          }}
        />
        <Typography
          variant={isMobile ? "h6" : "h5"}
          gutterBottom
          sx={{
            color: 'grey.600',
            fontSize: isMobile ? '1.25rem' : '1.5rem',
            mb: 2,
            textAlign: 'center',
            width: '100%',
          }}
        >
          No Catalogs Available
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'grey.500',
            fontSize: isMobile ? '0.875rem' : '1rem',
            mb: 3,
            maxWidth: 400,
            textAlign: 'center',
            mx: 'auto',
          }}
        >
          There are no price lists or catalogs available for this category at the moment.
          Please check back later or contact support for more information.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          onClick={handleBack}
          size={isMobile ? "medium" : "large"}
          sx={{
            mt: 2,
          }}
        >
          {isMobile ? "Go Back" : "Back to Categories"}
        </Button>
      </Paper>
    </Box>
  );

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !supplier) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, px: isMobile ? 2 : 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{ mb: 3 }}
          size={isMobile ? "small" : "medium"}
        >
          {isMobile ? "Back" : "Back to Suppliers"}
        </Button>
        <Alert severity="error">
          {error || 'Supplier not found'}
        </Alert>
      </Container>
    );
  }

  const groupedFiles = groupFilesByCategory(supplier.documents);
  const totalFiles = getTotalFileCount();
  const selectedCount = getSelectedFileCount();

  // Check if there are no documents
  if (!supplier.documents || supplier.documents.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 2 : 4 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={handleBack}
          sx={{ mb: 3 }}
          size={isMobile ? "small" : "medium"}
        >
          {isMobile ? "Back" : "Back to Categories"}
        </Button>

        <Grid container spacing={isMobile ? 2 : 4}>
          <Grid item xs={12}>
            <Typography 
              variant={isMobile ? "h6" : "h5"} 
              gutterBottom 
              sx={{ 
                color: 'primary.main',
                fontSize: isMobile ? '1.25rem' : '1.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                textTransform: 'uppercase'
              }}
            >
              <Store fontSize={isMobile ? "small" : "medium"} />
              {supplier.category}
            </Typography>
            <Typography 
              variant="body1" 
              paragraph 
              sx={{ 
                mb: 4,
                fontSize: isMobile ? '0.875rem' : '1rem',
                color: 'text.secondary'
              }}
            >
              Download the latest price lists and product catalogs. All documents are updated regularly with current pricing and product information.
            </Typography>
          </Grid>
          {renderNoCatalogsMessage()}
        </Grid>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: isMobile ? 2 : 4, px: isMobile ? 2 : 4 }}>
      <Button
        startIcon={<ArrowBack />}
        onClick={handleBack}
        sx={{ mb: 3 }}
        size={isMobile ? "small" : "medium"}
      >
        {isMobile ? "Back" : "Back to Categories"}
      </Button>

      <Grid container spacing={isMobile ? 2 : 4}>

        <Grid item xs={12} md={8}>
          <Typography 
            variant={isMobile ? "h6" : "h5"} 
            gutterBottom 
            sx={{ 
              color: 'primary.main',
              fontSize: isMobile ? '1.25rem' : '1.5rem',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Store fontSize={isMobile ? "small" : "medium"} />
            {supplier.category}
          </Typography>
          <Typography 
            variant="body1" 
            paragraph 
            sx={{ 
              mb: 4,
              fontSize: isMobile ? '0.875rem' : '1rem',
              color: 'text.secondary'
            }}
          >
            Download the latest price lists and product catalogs. All documents are updated regularly with current pricing and product information.
          </Typography>

          {/* Download All Button */}
          {totalFiles > 0 && (
            <Card sx={{ mb: 3, backgroundColor: 'primary.lighter' }}>
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: isMobile ? 'column' : 'row',
                  justifyContent: 'space-between', 
                  alignItems: isMobile ? 'flex-start' : 'center',
                  gap: isMobile ? 2 : 0,
                  mb: 2 
                }}>
                  <Box sx={{ width: isMobile ? '100%' : 'auto' }}>
                    <Typography 
                      variant={isMobile ? "subtitle1" : "h6"} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1,
                        fontSize: isMobile ? '1rem' : '1.25rem'
                      }}
                    >
                      <DownloadForOffline fontSize={isMobile ? "small" : "medium"} />
                      Bulk Download All Documents
                    </Typography>
                    <Typography 
                      variant="body2" 
                      color="text.secondary"
                      sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                    >
                      {selectedCount} of {totalFiles} documents selected for download
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={downloading.all ? 
                      <CircularProgress size={isMobile ? 16 : 20} /> : 
                      <Download fontSize={isMobile ? "small" : "medium"} />
                    }
                    onClick={handleDownloadAllAsZip}
                    disabled={downloading.all || selectedCount === 0}
                    size={isMobile ? "small" : "large"}
                    fullWidth={isMobile}
                    sx={{ 
                      minWidth: isMobile ? '100%' : 'auto',
                      mt: isMobile ? 1 : 0,
                      backgroundColor: 'primary.main',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      }
                    }}
                  >
                    {downloading.all ? 'Creating ZIP...' : 
                      isMobile ? `Download (${selectedCount})` : 'Download Selected as ZIP'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Price Lists Category */}
          {groupedFiles['Price Lists'] && selectedFiles['Price Lists'] && (
            <Card sx={{ mb: 3, backgroundColor: 'primary.lighter' }}>
              <CardContent sx={{ p: isMobile ? 2 : 3 }}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: isMobile ? 'column' : 'row',
                  justifyContent: 'space-between', 
                  alignItems: isMobile ? 'flex-start' : 'center',
                  gap: isMobile ? 2 : 0,
                  mb: 2 
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'flex-start' : 'center', 
                    gap: isMobile ? 1 : 2,
                    width: isMobile ? '100%' : 'auto'
                  }}>
                    <Box>
                      <Typography 
                        variant={isMobile ? "subtitle1" : "h6"} 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: 1,
                          flexWrap: 'wrap',
                          fontSize: isMobile ? '1rem' : '1.25rem'
                        }}
                      >
                        <AttachMoney fontSize={isMobile ? "small" : "medium"} color="primary" />
                        Price Lists
                        <Chip 
                          label={`${groupedFiles['Price Lists'].length} documents`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                      >
                        Current pricing for all products. Includes bulk pricing and special offers.
                      </Typography>
                    </Box>
                    
                    <FormControlLabel
                      control={
                        <Checkbox
                          size={isMobile ? "small" : "medium"}
                          checked={selectedFiles['Price Lists']?.every(Boolean)}
                          onChange={(e) => toggleAllFilesInCategory('Price Lists', e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Typography 
                          variant={isMobile ? "caption" : "body2"}
                          sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                        >
                          Select All Price Lists
                        </Typography>
                      }
                    />
                  </Box>
                  
                  {groupedFiles['Price Lists'].length > 1 && (
                    <Button
                      variant="outlined"
                      size={isMobile ? "small" : "medium"}
                      color="primary"
                      startIcon={downloading['Price Lists_zip'] ? 
                        <CircularProgress size={isMobile ? 12 : 16} /> : 
                        <Download fontSize={isMobile ? "small" : "medium"} />
                      }
                      onClick={() => handleDownloadCategoryAsZip('Price Lists', groupedFiles['Price Lists'])}
                      disabled={downloading['Price Lists_zip'] || selectedFiles['Price Lists']?.filter(Boolean).length === 0}
                      fullWidth={isMobile}
                      sx={{ 
                        minWidth: isMobile ? '100%' : 'auto',
                        mt: isMobile ? 1 : 0
                      }}
                    >
                      {downloading['Price Lists_zip'] ? 'Creating ZIP...' : 
                        isMobile ? 'Download All' : 'Download Selected Price Lists'}
                    </Button>
                  )}
                </Box>

                <Divider sx={{ mb: 2 }} />

                <List sx={{ p: 0 }}>
                  {groupedFiles['Price Lists'].map((file, index) => (
                    <React.Fragment key={file.id}>
                      <ListItem
                        alignItems="flex-start"
                        sx={{
                          flexDirection: isMobile ? 'column' : 'row',
                          alignItems: isMobile ? 'flex-start' : 'center',
                          p: isMobile ? '12px 0' : '16px 0',
                          width: '100%'
                        }}
                        secondaryAction={
                          <Box sx={{ 
                            display: 'flex', 
                            gap: 1,
                            mt: isMobile ? 2 : 0,
                            width: isMobile ? '100%' : 'auto',
                            justifyContent: isMobile ? 'flex-end' : 'flex-start'
                          }}>
                            <Tooltip title={file.fileType === 'pdf' ? "View PDF" : "View Excel"}>
                              <IconButton
                                edge="end"
                                onClick={() => window.open(file.url, '_blank')}
                                size={isMobile ? "small" : "medium"}
                                color="primary"
                              >
                                <Visibility fontSize={isMobile ? "small" : "medium"} />
                              </IconButton>
                            </Tooltip>
                            <Button
                              variant="outlined"
                              size={isMobile ? "small" : "medium"}
                              color="primary"
                              startIcon={downloading[`Price Lists_${index}`] ? 
                                <CircularProgress size={isMobile ? 12 : 16} /> : 
                                <Download fontSize={isMobile ? "small" : "medium"} />
                              }
                              onClick={() => handleDownloadSingle(file.url, file.name, 'Price Lists', index)}
                              disabled={downloading[`Price Lists_${index}`]}
                              sx={{ 
                                minWidth: isMobile ? 'auto' : '100px',
                                flex: isMobile ? 1 : 'none'
                              }}
                            >
                              {downloading[`Price Lists_${index}`] ? 
                                isMobile ? '...' : 'Downloading...' : 
                                isMobile ? 'Get' : 'Download'
                              }
                            </Button>
                          </Box>
                        }
                      >
                        <Box sx={{ 
                          display: 'flex', 
                          width: '100%',
                          alignItems: 'center',
                          mb: isMobile ? 1 : 0
                        }}>
                          <ListItemIcon sx={{ minWidth: isMobile ? 36 : 48 }}>
                            <Checkbox
                              edge="start"
                              size={isMobile ? "small" : "medium"}
                              checked={!!selectedFiles['Price Lists']?.[index]}
                              onChange={() => toggleFileSelection('Price Lists', index)}
                              tabIndex={-1}
                              disableRipple
                              color="primary"
                            />
                          </ListItemIcon>
                          <ListItemIcon sx={{ 
                            minWidth: isMobile ? 40 : 56,
                            mr: isMobile ? 1 : 2
                          }}>
                            {getFileIcon(file)}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography 
                                  variant={isMobile ? "body2" : "body1"}
                                  sx={{ 
                                    fontWeight: 'medium',
                                    fontSize: isMobile ? '0.875rem' : '1rem'
                                  }}
                                >
                                  {file.name}
                                </Typography>
                                {file.version && (
                                  <Chip 
                                    label={file.version}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box component="span" sx={{ 
                                display: 'flex', 
                                flexDirection: 'column',
                                fontSize: isMobile ? '0.75rem' : '0.875rem',
                                mt: 0.5
                              }}>
                                <span><strong>Last Updated:</strong> {formatDate(file.uploadedAt)}</span>
                                <span><strong>Size:</strong> {file.size} • <strong>Type:</strong> {file.fileType.toUpperCase()}</span>
                              </Box>
                            }
                            sx={{ 
                              m: 0,
                              '& .MuiListItemText-secondary': {
                                fontSize: isMobile ? '0.75rem' : '0.875rem'
                              }
                            }}
                          />
                        </Box>
                      </ListItem>
                      {index < groupedFiles['Price Lists'].length - 1 && <Divider variant="inset" component="li" />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Container>
  );
};

export default PriceListCatalogPage;